import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CiLogout } from "react-icons/ci";
import { PiArrowSquareOut, PiTrash, PiCaretDown, PiQuestion, PiGearSix } from "react-icons/pi";

import { logInvestingActivity } from "../utils/investingActivity";

/**
 * Shared account dropdown for the investing app (Help / Sign out / Delete my
 * data). Used by both LayoutInvesting (onboarding flow) and InvestingDashboard so
 * the menu can't drift between them.
 *
 * Props:
 *  - userEmail, userInitial, userLabel: account display
 *  - onLogout, onDeleteClick: handlers
 *  - showLabel: show the username next to the avatar (desktop layout header)
 *  - showCaret: show the caret-down chevron (layout header). The dashboard uses
 *    an avatar-only trigger, so it passes showCaret={false}.
 */
export const InvestingAccountMenu = ({
  userEmail,
  userInitial,
  userLabel,
  onLogout,
  onDeleteClick,
  onSwitchToMainApp,
  showLabel = false,
  showCaret = true,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => {
          if (!open) logInvestingActivity("investing_account_menu_opened", {});
          setOpen((v) => !v);
        }}
        className={`flex items-center gap-1.5 md:gap-2 transition-colors cursor-pointer ${
          showLabel || showCaret ? "hover:bg-[#F5F5F5] rounded-lg px-2 py-1.5" : ""
        }`}
      >
        <div className="w-8 h-8 rounded-full bg-black flex items-center justify-center">
          <span className="text-white text-xs font-medium">{userInitial}</span>
        </div>
        {showLabel && userEmail && (
          <span className="text-sm text-[#1c1c1c] font-medium max-w-[120px] truncate">
            {userLabel}
          </span>
        )}
        {showCaret && (
          <PiCaretDown className={`w-3.5 h-3.5 text-[#717171] transition-transform ${open ? "rotate-180" : ""}`} />
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-lg border border-[#E5E5E5] py-2 z-50"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {userEmail && (
            <>
              <div className="px-4 py-2">
                <p className="text-sm font-medium text-[#1c1c1c]">Account</p>
                <p className="text-xs text-[#717171] truncate">{userEmail}</p>
              </div>
              <div className="border-t border-[#E5E5E5] my-1" />
            </>
          )}

          {/* Settings (two-step verification + password) */}
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate("/setting");
            }}
            className="w-full px-4 py-2.5 text-left text-sm text-[#1c1c1c] hover:bg-[#F5F5F5] transition-colors flex items-center gap-2 cursor-pointer"
          >
            <PiGearSix className="w-4 h-4" />
            Settings
          </button>

          <a
            href="mailto:help@getprimemeridian.com"
            onClick={() => {
              // Reaching for support is a signal something in the app failed
              // the user, so it belongs in their timeline.
              logInvestingActivity("investing_help_clicked", {}, { flushNow: true });
              setOpen(false);
            }}
            className="w-full px-4 py-2.5 text-left text-sm text-[#1c1c1c] hover:bg-[#F5F5F5] transition-colors flex items-center gap-2 cursor-pointer"
          >
            <PiQuestion className="w-4 h-4" />
            Help
          </a>

          {onSwitchToMainApp && (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onSwitchToMainApp();
              }}
              className="w-full px-4 py-2.5 text-left text-sm text-[#1c1c1c] hover:bg-[#F5F5F5] transition-colors flex items-center gap-2 cursor-pointer"
            >
              <PiArrowSquareOut className="w-4 h-4" />
              Switch to Tax App
            </button>
          )}

          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
            className="w-full px-4 py-2.5 text-left text-sm text-[#1c1c1c] hover:bg-[#F5F5F5] transition-colors flex items-center gap-2 cursor-pointer"
          >
            <CiLogout className="w-4 h-4" />
            Sign out
          </button>

          {onDeleteClick && (
            <>
              <div className="border-t border-[#E5E5E5] my-1" />

              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onDeleteClick();
                }}
                className="w-full px-4 py-2.5 text-left text-sm text-[#DC2626] hover:bg-[#FEF2F2] transition-colors flex items-center gap-2 cursor-pointer"
              >
                <PiTrash className="w-4 h-4" />
                Delete my data
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Shared "delete your investing data" confirmation modal, requiring the user to
 * type "confirm delete". Used by LayoutInvesting and InvestingDashboard.
 */
export const InvestingDeleteDataModal = ({
  show,
  isDeleting,
  confirmText,
  setConfirmText,
  onConfirm,
  onCancel,
}) => {
  if (!show) return null;
  const canDelete = confirmText === "confirm delete";
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 md:p-8 max-w-sm w-full">
        <h2 className="font-semibold text-xl text-[#1c1c1c] mb-4">
          Delete your investing data?
        </h2>
        <p className="text-sm text-[#717171] mb-4">This action cannot be undone.</p>
        <p className="text-sm text-[#717171] mb-4">
          This deletes your investing data — your overview, uploaded documents, chats, and
          simulations — and you'll start over from the upload screen. Your main Prime Meridian tax filing data is{" "}
          <b>not</b> affected.
        </p>
        <p className="text-sm text-[#717171] mb-4 italic">
          Your data is never sold, and is never used for AI model training anywhere. Your data is stored on Prime
          Meridian servers only for your own visibility, and can be deleted at any time by pressing this button.
        </p>

        <div className="mb-6">
          <label className="block text-sm text-[#717171] mb-2">
            To proceed, please type <span className="font-semibold text-[#1c1c1c]">confirm delete</span>
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="confirm delete"
            disabled={isDeleting}
            className="w-full px-4 py-2.5 text-sm border border-[#E5E5E5] rounded-lg focus:outline-none focus:border-[#DC2626] transition-colors disabled:opacity-50"
          />
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isDeleting}
            className="flex-1 tracking-wide text-[#1c1c1c] font-medium text-base rounded-lg py-3 text-center bg-[#E5E5E5] hover:bg-[#D4D4D4] transition-colors cursor-pointer disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isDeleting || !canDelete}
            className={`flex-1 tracking-wide text-white font-medium text-base rounded-lg py-3 text-center transition-colors disabled:opacity-50 ${
              canDelete ? "bg-[#DC2626] hover:bg-[#B91C1C] cursor-pointer" : "bg-[#DC2626]/30 cursor-not-allowed"
            }`}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
};
